/**
 * Переходы между экранами вертикального среза.
 *
 * Вынесено из App, чтобы корневой компонент оставался композицией, а не
 * вместилищем всей логики приложения (ТЗ NFR-Q-03).
 */

import { useCallback, useEffect, useState } from 'react';
import type { EntitlementSnapshot, OsPlatform, ThemePreference } from '@type2x/shared-contracts';
import { DEFAULT_LAYOUT_BY_LANGUAGE, DEFAULT_PLATFORM } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import type { IntegritySignals } from '@type2x/adapter-web';
import type { ControlTask } from '@type2x/feature-control-test';
import {
  DEFAULT_START_PATH,
  activeProfile,
  setStartPath,
  type StartPath,
} from '@type2x/feature-personal-workspace';
import type { OnboardingChoice } from '@type2x/feature-onboarding';
import type { SymbolMode } from '@type2x/feature-symbols';
import type { PackMode } from '@type2x/feature-packs';
import type { TempoMode } from '@type2x/feature-tempo';
import type { RegistrationOutcome, SignedIn } from '@type2x/feature-accounts';
import {
  recordSession,
  recordVerifiedResult,
  setEntitlements,
  type Workspace,
} from '@type2x/feature-personal-workspace';

import type { Runtime } from '../runtime.js';
import { INITIAL_SCREEN, type AppScreen, type LockedTarget } from '../state/app-state.js';
import { useSync } from './useSync.js';
import { useAccountActions } from './useAccountActions.js';
import { useProfileSync } from './useProfileSync.js';
import { useAppearanceActions } from './useAppearance.js';
import { useLearningActions } from './useLearning.js';
import { useVerificationLink } from './useVerificationLink.js';
import { useControlTest } from './useControlTest.js';
import { useMistakes } from './useMistakes.js';
import { useSentences } from './useSentences.js';
import { useFundamentals } from './useFundamentals.js';
import { useSymbols } from './useSymbols.js';
import { usePacks } from './usePacks.js';
import { useLayoutSwitch } from './useLayoutSwitch.js';
import { useTempo } from './useTempo.js';
import { useCustomText } from './useCustomText.js';
import {
  latinReadiness,
  layoutSwitchReadiness,
  scriptReadiness,
  type ScriptReadiness,
} from '../state/script-readiness.js';
import { useLanguage, type LanguageActions } from './useLanguage.js';
import { mistakesAvailable } from '../state/mistakes.js';
import { availableSymbolModes } from '../state/symbols.js';
import { availablePackModes, packsScreen } from '../state/packs.js';
import { layoutSwitchScreen } from '../state/layout-switch.js';
import { reportScreen } from '../state/report.js';
import { achievementsScreen } from '../state/achievements.js';
import type { VerificationState } from '../state/verification.js';
import {
  fundamentalsAvailable,
  resumeScreen,
  resumesToFundamentals,
} from '../state/fundamentals.js';

/** Действия, которые приложение передаёт экранам. */
export interface AppFlow extends LanguageActions {
  readonly screen: AppScreen;
  readonly onOnboardingDone: (choice: OnboardingChoice) => void;
  readonly onDiagnosticsDone: (words: readonly string[], session: TypingSession) => void;
  readonly onPracticeDone: (session: TypingSession) => void;
  /** Продолжить с итога диагностики туда, куда ведёт рекомендация. */
  readonly onContinueAfterDiagnostics: () => void;
  /** Пройти диагностику заново: одно измерение могло соврать. */
  readonly onRetakeDiagnostics: () => void;
  readonly onAgain: () => void;
  readonly onSaveProgress: () => void;
  readonly onRegister: (email: string, password: string) => Promise<RegistrationOutcome>;
  /** Вход в существующий аккаунт с переносом локального прогресса. */
  readonly onLogin: (email: string, password: string) => Promise<SignedIn>;
  /** Спрашивает сервер, кто вошёл: состояние аккаунта переживает перезагрузку. */
  readonly onReadSession: () => Promise<SignedIn | null>;
  /** Просит сервер отправить письмо подтверждения ещё раз. */
  readonly onResendVerification: (email: string) => Promise<void>;
  /** Просит сервер прислать ссылку восстановления пароля. */
  readonly onRequestPasswordReset: (email: string) => Promise<void>;
  /** Меняет пароль по токену из письма. */
  readonly onConfirmPasswordReset: (token: string, password: string) => Promise<void>;
  /** Забирает выгрузку прогресса файлом. */
  readonly onExportProgress: () => Promise<void>;
  /** Удаляет аккаунт и забывает локальное пространство. */
  readonly onDeleteAccount: (password: string) => Promise<void>;
  /**
   * Выходит из аккаунта; `pending` — очередь занятий не разобрана.
   *
   * Выход не спрашивает подтверждения: терять нечего, навык вернётся при
   * следующем входе. Кроме одного случая — когда итоги последних занятий ещё
   * не ушли на сервер: их не вернёт никто, и об этом говорится вслух.
   */
  readonly onLogout: (force?: boolean) => Promise<'done' | 'pending'>;
  readonly onConsentChange: (optIn: boolean) => void;
  /** Состояние подтверждения адреса по ссылке из письма. */
  readonly verification: VerificationState;
  /** Возврат к тренировке после подтверждения адреса. */
  readonly onContinueAfterVerification: () => void;
  readonly onStartControlTest: () => void;
  readonly onControlTestDone: (session: TypingSession, signals: IntegritySignals) => void;
  readonly onBackToPractice: () => void;
  readonly retentionDue: boolean;
  readonly onStartMistakes: () => void;
  readonly onMistakeStageDone: (run: TypingSession) => void;
  readonly onStartSentences: () => void;
  readonly onSentenceDone: (run: TypingSession) => void;
  readonly onStartFundamentals: () => void;
  readonly onFundamentalsDone: (run: TypingSession) => void;
  readonly onStartSymbols: (mode: SymbolMode) => void;
  readonly onSymbolsDone: (run: TypingSession) => void;
  readonly onStartPacks: (mode: PackMode) => void;
  readonly onPacksDone: (run: TypingSession) => void;
  readonly onOpenReport: () => void;
  /** Открывает достижения, серию и личные рекорды. */
  readonly onOpenAchievements: () => void;
  /** Открывает рейтинг: таблицу лидеров языка тренировки. */
  readonly onOpenLeaderboard: () => void;
  readonly onThemeChange: (theme: ThemePreference) => void;
  readonly onFocusModeChange: (focusMode: boolean) => void;
  /** Включает и выключает звук ошибки на экране набора. */
  readonly onSoundOnErrorChange: (soundOnError: boolean) => void;
  /** Включает и выключает игровые элементы целиком (ТЗ FR-GM-08). */
  readonly onGameElementsChange: (gameElements: boolean) => void;
  readonly onStartLayoutSwitch: () => void;
  readonly onLayoutSwitchDone: (run: TypingSession) => void;
  readonly onStartTempo: (mode: TempoMode) => void;
  readonly onTempoRunDone: (run: TypingSession) => void;
  readonly onStartCustomText: () => void;
  readonly onCustomTextSubmit: (text: string) => void;
  readonly onCustomTextDone: (run: TypingSession) => void;
  /** Есть ли слабые места, ради которых стоит открывать режим «Мои ошибки». */
  readonly mistakesAvailable: boolean;
  /** Остались ли непройденные группы клавиш в режиме «Основы». */
  readonly fundamentalsAvailable: boolean;
  /**
   * Ведёт ли основная колея в «Основы», а не в персональную тренировку.
   *
   * Нужен экрану результата: кнопка обязана называть, куда ведёт.
   */
  readonly resumesToFundamentals: boolean;
  /** Режимы символов, которым ещё есть чему учить. */
  readonly symbolModes: readonly SymbolMode[];
  /** Режимы пакетов, которым ещё есть чему учить. */
  readonly packModes: readonly PackMode[];
  /** Освоена ли латиница настолько, чтобы материал пакетов был посилен. */
  readonly latin: ScriptReadiness;
  /** Освоены ли обе раскладки режима переключения. */
  readonly layoutSwitch: ScriptReadiness;
  /**
   * Освоение собственной раскладки: сколько её букв набирается уверенно.
   *
   * Это и есть измеримый ответ на «сколько я уже прошёл» для карточки
   * продолжения (ТЗ UI-41). Считается тем же кодом, что и условия чужих
   * раскладок, — второй счёт разошёлся бы с первым.
   */
  readonly keys: ScriptReadiness;
  /** Операционная система профиля: от неё зависит расположение клавиш. */
  readonly platform: OsPlatform;
  /** Возврат на предыдущий экран; из начала истории — на главную. */
  readonly onBack: () => void;
  /** Есть ли куда возвращаться: на первом входе и на главной кнопка не нужна. */
  readonly canGoBack: boolean;
  /** Шаг вперёд после возврата: пользователь мог передумать. */
  readonly onForward: () => void;
  readonly canGoForward: boolean;
  readonly onOpenHome: () => void;
  /** С чего начато обучение текущему языку. */
  readonly startPath: StartPath;
  readonly onStartPathChange: (path: StartPath) => void;
  /** Открыть закрытый режим всё равно: предупреждение уже прочитано. */
  readonly onProceedLocked: (target: LockedTarget) => void;
}

/** Состояние загрузки и сохранения пространства. */
export interface WorkspaceAccess {
  readonly workspace: Workspace | null;
  readonly loading: boolean;
  readonly commit: (next: Workspace) => Promise<void>;
  /** Забывает пространство целиком: используется при удалении аккаунта. */
  readonly forget: () => Promise<void>;
}

/**
 * Сколько переходов помнит «Назад».
 *
 * Глубина конечная намеренно: история нужна, чтобы выйти из занятия, а не
 * чтобы восстанавливать вчерашний путь. Дальше начала истории «Назад» ведёт на
 * главную — там есть всё.
 */
const HISTORY_DEPTH = 20;

/** Экраны, которые в историю не попадают: возвращаться на них некуда. */
const TRANSIENT_SCREENS = new Set(['loading', 'onboarding']);

export function useAppFlow(runtime: Runtime, access: WorkspaceAccess): AppFlow {
  const { workspace, loading, commit } = access;
  const [screen, showScreen] = useState<AppScreen>(INITIAL_SCREEN);
  const [past, setPast] = useState<readonly AppScreen[]>([]);
  const [future, setFuture] = useState<readonly AppScreen[]>([]);

  /**
   * Переход с запоминанием того, откуда пришли.
   *
   * Все переходы приложения идут через эту функцию, поэтому «Назад» и «Вперёд»
   * работают на любом экране без того, чтобы каждый режим о них знал
   * (ТЗ FR-EN-57). Новый переход обрывает будущее: вернувшись назад и уйдя
   * вбок, вперёд идти уже некуда — там другая ветка, которой пользователь не
   * выбирал.
   */
  const setScreen = useCallback(
    (next: AppScreen) => {
      setPast((visited) =>
        TRANSIENT_SCREENS.has(screen.name) || screen.name === next.name
          ? visited
          : [...visited, screen].slice(-HISTORY_DEPTH),
      );
      setFuture([]);
      showScreen(next);
    },
    [screen],
  );

  /** Возврат на предыдущий экран, а из начала истории — на главную. */
  const onBack = useCallback(() => {
    const previous = past.at(-1);
    setPast((visited) => visited.slice(0, -1));
    // Тот экран, с которого уходим, становится «вперёд»: передумать можно.
    setFuture((ahead) => [screen, ...ahead].slice(0, HISTORY_DEPTH));
    showScreen(previous ?? { name: 'home' });
  }, [past, screen]);

  /** Шаг вперёд: отменяет «Назад», если пользователь передумал. */
  const onForward = useCallback(() => {
    const next = future[0];
    if (next === undefined) return;

    setFuture((ahead) => ahead.slice(1));
    setPast((visited) => [...visited, screen].slice(-HISTORY_DEPTH));
    showScreen(next);
  }, [future, screen]);

  const onOpenHome = useCallback(() => setScreen({ name: 'home' }), [setScreen]);

  /**
   * Открывает режим вопреки невыполненному условию.
   *
   * Условие — совет, а не стена: пользователь прочитал, чем это обернётся, и
   * решил посмотреть. Запрет «даже не пробуй» он всё равно обойдёт — сменой
   * языка тренировки или обидой на приложение.
   */
  const onProceedLocked = useCallback(
    (target: LockedTarget) => {
      if (workspace === null) return;
      setScreen(
        target.kind === 'packs'
          ? packsScreen(runtime, workspace, target.mode)
          : layoutSwitchScreen(runtime, workspace),
      );
    },
    [runtime, workspace, setScreen],
  );

  /**
   * Меняет то, с чего идёт обучение языку, и сразу открывает выбранное.
   *
   * Выбор сделан на первом входе и там же был заперт: сменить его было нельзя
   * ниоткуда. Смена ничего не стирает — прогресс остаётся, меняется только то,
   * куда ведёт «продолжить».
   */
  const onStartPathChange = useCallback(
    (path: StartPath) => {
      if (workspace === null) return;

      const next = setStartPath(workspace, path, runtime.clock.isoNow());
      void commit(next);
      setScreen(resumeScreen(runtime, next));
    },
    [runtime, workspace, commit, setScreen],
  );

  const onEntitlements = useCallback(
    (snapshot: EntitlementSnapshot) => {
      if (workspace === null) return;
      void commit(setEntitlements(workspace, snapshot, runtime.clock.isoNow()));
    },
    [runtime, workspace, commit],
  );

  const sync = useSync(runtime, onEntitlements);

  // Профиль языка едет отдельно от очереди: он состояние, а не событие
  // (docs/HANDOFF.md, этап ABO).
  useProfileSync(runtime, workspace);

  // Пространство загружено: новый гость идёт на первый вход, вернувшийся —
  // сразу к персональной тренировке по сохранённому прогрессу.
  useEffect(() => {
    if (loading || screen.name !== 'loading') return;
    setScreen(workspace === null ? { name: 'onboarding' } : resumeScreen(runtime, workspace));
  }, [loading, workspace, screen.name, runtime]);

  const verification = useVerificationLink(runtime, {
    workspace,
    loading,
    commit,
    flush: sync.flush,
    setScreen,
  });

  // Пришли по приглашению — открывается рейтинг: человек нажал ссылку «добавь
  // меня в друзья», и показать ему что-то другое значило бы не ответить.
  // Добавления при этом не происходит: приглашение ждёт согласия.
  useEffect(() => {
    if (loading || runtime.friendInvite === null) return;
    setScreen({ name: 'leaderboard' });
  }, [runtime, loading]);

  // Ссылка восстановления ведёт на свой экран: подтверждать нечего, новый
  // пароль задаёт человек.
  useEffect(() => {
    const link = runtime.emailLink;
    if (loading || link === null || link.kind !== 'reset-password') return;
    setScreen({ name: 'password-reset', token: link.token });
  }, [runtime, loading]);

  const appearance = useAppearanceActions(runtime, access);
  const learning = useLearningActions(runtime, access, sync, setScreen, screen);
  const account = useAccountActions(runtime, access, sync, workspace?.uiLanguage ?? 'ru');
  const language = useLanguage(runtime, { workspace, commit, screen, setScreen });
  const control = useControlActions(runtime, access, setScreen, screen);
  const modes = useModeActions(runtime, {
    workspace,
    commit,
    screen,
    setScreen,
    submit: sync.submit,
  });

  const onAgain = useCallback(() => {
    if (workspace === null) return;
    setScreen(resumeScreen(runtime, workspace));
  }, [runtime, workspace]);

  const onSaveProgress = useCallback(() => setScreen({ name: 'account' }), []);

  // После подтверждения адреса возвращаемся туда, где человек и был: к
  // тренировке, а у пришедшего по ссылке без пространства — к первому входу.
  const onContinueAfterVerification = useCallback(() => {
    setScreen(workspace === null ? { name: 'onboarding' } : resumeScreen(runtime, workspace));
  }, [runtime, workspace]);

  // Отчёт ничего не меняет: он читает состояние и показывает его. Свежий
  // срез строится при каждом открытии — статистика к этому моменту уже другая.
  const onOpenReport = useCallback(() => {
    if (workspace === null) return;
    setScreen(reportScreen(runtime, workspace));
  }, [runtime, workspace]);

  // Рейтинг ничего не считает на устройстве: экран спрашивает готовый снимок
  // у сервера, а согласие на участие — договор с сервером, а не галочка в
  // браузере (ТЗ FR-LB-06).
  const onOpenLeaderboard = useCallback(() => {
    setScreen({ name: 'leaderboard' });
  }, []);

  const onOpenAchievements = useCallback(() => {
    if (workspace === null) return;
    setScreen(achievementsScreen(runtime, workspace));
  }, [runtime, workspace]);

  return {
    screen: loading ? INITIAL_SCREEN : screen,
    onAgain,
    onSaveProgress,
    verification,
    onContinueAfterVerification,
    onOpenReport,
    onOpenAchievements,
    onOpenLeaderboard,
    mistakesAvailable: mistakesAvailable(workspace),
    fundamentalsAvailable: fundamentalsAvailable(workspace),
    resumesToFundamentals: workspace !== null && resumesToFundamentals(workspace),
    symbolModes: availableSymbolModes(workspace),
    packModes: availablePackModes(workspace),
    onBack,
    canGoBack: screen.name !== 'onboarding' && screen.name !== 'loading' && screen.name !== 'home',
    onForward,
    canGoForward: future.length > 0,
    onOpenHome,
    onProceedLocked,
    startPath: workspace === null ? DEFAULT_START_PATH : activeProfile(workspace).startPath,
    onStartPathChange,
    latin: latinReadiness(workspace),
    layoutSwitch: layoutSwitchReadiness(workspace),
    keys: scriptReadiness(
      workspace,
      workspace === null
        ? DEFAULT_LAYOUT_BY_LANGUAGE.ru
        : activeProfile(workspace).keyboard.layoutId,
    ),
    platform: workspace === null ? DEFAULT_PLATFORM : activeProfile(workspace).keyboard.platform,
    ...language,
    ...appearance,
    ...learning,
    ...account,
    ...control,
    ...modes,
  };
}

/**
 * Короткие режимы, доступные с экрана результата.
 *
 * Каждому нужен один и тот же набор зависимостей, поэтому они собираются
 * вместе: корневой хук остаётся композицией (ТЗ NFR-Q-03).
 */
function useModeActions(
  runtime: Runtime,
  deps: {
    readonly workspace: Workspace | null;
    readonly commit: (next: Workspace) => Promise<void>;
    readonly screen: AppScreen;
    readonly setScreen: (screen: AppScreen) => void;
    readonly submit: ReturnType<typeof useSync>['submit'];
  },
): Pick<
  AppFlow,
  | 'onStartMistakes'
  | 'onMistakeStageDone'
  | 'onStartSentences'
  | 'onSentenceDone'
  | 'onStartFundamentals'
  | 'onFundamentalsDone'
  | 'onStartSymbols'
  | 'onSymbolsDone'
  | 'onStartPacks'
  | 'onPacksDone'
  | 'onStartLayoutSwitch'
  | 'onLayoutSwitchDone'
  | 'onStartTempo'
  | 'onTempoRunDone'
  | 'onStartCustomText'
  | 'onCustomTextSubmit'
  | 'onCustomTextDone'
> {
  return {
    ...useMistakes(runtime, deps),
    ...useSentences(runtime, deps),
    ...useFundamentals(runtime, deps),
    ...useSymbols(runtime, deps),
    ...usePacks(runtime, deps),
    ...useLayoutSwitch(runtime, deps),
    ...useTempo(runtime, deps),
    ...useCustomText(runtime, deps),
  };
}

/** Контрольный тест и отложенная проверка сохранения навыка. */
function useControlActions(
  runtime: Runtime,
  access: WorkspaceAccess,
  setScreen: (screen: AppScreen) => void,
  screen: AppScreen,
): Pick<AppFlow, 'onStartControlTest' | 'onControlTestDone' | 'onBackToPractice' | 'retentionDue'> {
  const { workspace, commit } = access;
  const language = workspace?.activeLanguage ?? 'ru';
  const control = useControlTest(runtime, language);

  const onStartControlTest = useCallback(() => {
    if (workspace === null) return;
    const profile = activeProfile(workspace);

    setScreen({
      name: 'control-test',
      task: control.buildTask(profile.language, profile.keyboard.layoutId),
      sessionId: runtime.ids.newSessionId(),
    });
  }, [runtime, workspace, control, setScreen]);

  const onControlTestDone = useCallback(
    (session: TypingSession, signals: IntegritySignals) => {
      if (screen.name !== 'control-test') return;
      const task: ControlTask = screen.task;

      // Контрольный тест — тоже занятие: он идёт в объём набора, в день
      // практики и в рекорды наравне с остальными.
      const now = runtime.clock.isoNow();
      if (workspace !== null) void commit(recordSession(workspace, session.metrics, now));

      // Экран вердикта показывается сразу: ответ сервера подставляется, когда
      // придёт, и его ожидание не блокирует интерфейс.
      setScreen({ name: 'control-result', outcome: null, metrics: session.metrics });
      void control.submit(task, session, signals).then((outcome) => {
        setScreen({ name: 'control-result', outcome, metrics: session.metrics });

        // Подтверждает результат сервер, а не клиент: рекорд записывается
        // только по принятому вердикту.
        if (outcome.kind === 'verdict' && outcome.verdict.accepted && workspace !== null) {
          void commit(
            recordVerifiedResult(
              recordSession(workspace, session.metrics, now),
              session.metrics.wpm,
              runtime.clock.isoNow(),
            ),
          );
        }
      });
    },
    [runtime, workspace, commit, screen, control, setScreen],
  );

  const onBackToPractice = useCallback(() => {
    if (workspace === null) return;
    setScreen(resumeScreen(runtime, workspace));
  }, [runtime, workspace, setScreen]);

  return {
    onStartControlTest,
    onControlTestDone,
    onBackToPractice,
    retentionDue: control.retentionDue,
  };
}
